import {FC, useCallback, useMemo} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {FullGroup, groupName, GroupType} from '@shared/models/api_models';

import {Button} from '@shared_web/components/core/button';
import {showModal} from '@shared_web/components/core/modal';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';

import {GenericConfirmModal} from '@mavismart_frontend/components/generic_confirm_modal';
import {Loader} from '@mavismart_frontend/components/loader';
import {Title} from '@mavismart_frontend/components/title';
import {Translate} from '@mavismart_frontend/components/translate';
import {UpdateGroupNameForm} from '@mavismart_frontend/components/updateGroupNameForm';
import {icon_home} from '@mavismart_frontend/icons/icon_home';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, MODAL_THEME, MODAL_TITLE_STYLE} from '@mavismart_frontend/lib/constants';
import {
  GenericInfoBlock,
  GenericInfoBlockItem,
} from '@mavismart_frontend/pages/cells/generic_info_block';
import {useAccount} from '@mavismart_frontend/stores/account_store';
import {setGroups, useGroups} from '@mavismart_frontend/stores/groups_store';

interface GroupInfoProps {
  group?: FullGroup;
}

export const GroupInfo: FC<GroupInfoProps> = ({group}) => {
  const fullGroups = useGroups();
  const parentGroup = fullGroups?.find(g => g.group.id === group?.group.parentGroupId);
  const account = useAccount();
  const [, navigate] = useLocation();

  const handleConfirmRemove = useCallback(async () => {
    if (!(account && group)) {
      return;
    }
    try {
      await apiCall('POST /remove-collaborator', {email: account.email, groupId: group.group.id});
      setGroups(fullGroups?.filter(g => g.group.id !== group.group.id));
      navigate('/');
    } catch (err) {
      notifyError(err);
    }
  }, [account, group, fullGroups, navigate]);

  const handleRemoveCollaborator = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="confirm_leave_group" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <GenericConfirmModal onConfirm={handleConfirmRemove} />,
    });
  }, [handleConfirmRemove]);

  const handleConfirmRemoveGroup = useCallback(async () => {
    if (!group) {
      return;
    }
    try {
      await apiCall('POST /delete-custom-group', {groupId: group.group.id});
      setGroups(fullGroups?.filter(g => g.group.id !== group.group.id));
      navigate('/');
    } catch (err) {
      notifyError(err);
    }
  }, [group, fullGroups, navigate]);

  const handleRemoveGroup = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="remove_the_group" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <GenericConfirmModal onConfirm={handleConfirmRemoveGroup} />,
    });
  }, [handleConfirmRemoveGroup]);

  const handleUpdateGroupName = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="update_the_name" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <UpdateGroupNameForm group={group} />,
    });
  }, [group]);

  const addressItems = useMemo<GenericInfoBlockItem[]>(
    () =>
      !group
        ? []
        : [
            {
              key: 'fulladdress',
              subtitle: (
                <div>
                  <div>
                    {group.group.address1 ? group.group.address1 : <></>}{' '}
                    {group.group.address2 ? group.group.address2 : <></>}{' '}
                    {group.group.address3 ? group.group.address3 : <></>}
                  </div>
                  <div>
                    {group.group.postalCode} {group.group.city}
                  </div>
                  <div>{group.group.country}</div>
                </div>
              ),
            },
          ],
    [group]
  );

  if (!group) {
    return (
      <Flex $align="center" $justify="center" $width="100%" $height="100%">
        <Loader />
      </Flex>
    );
  }

  return (
    <Wrapper>
      <Title name={groupName(group)} />
      {/* <div>{group.group.type}</div> */}
      {parentGroup && (
        <div>
          <Translate value="parent_group" /> {groupName(parentGroup)}
        </div>
      )}
      {group.group.type !== GroupType.Manual ? (
        <GenericInfoBlock
          icon={<SvgIcon icon={icon_home} size={22} color="transparent" />}
          items={addressItems}
        />
      ) : (
        <></>
      )}

      {group.permission === 'admin' && group.group.type === GroupType.Manual && (
        <WrapperButtons>
          <Button themeOverrides={BUTTON_THEME} onClick={handleRemoveGroup}>
            <Translate value="remove_the_group" />
          </Button>
          <Button themeOverrides={BUTTON_THEME} onClick={handleUpdateGroupName}>
            <Translate value="update_the_name" />
          </Button>
        </WrapperButtons>
      )}

      {group.permission !== 'admin' && (
        <WrapperButtons>
          <Button themeOverrides={BUTTON_THEME} onClick={handleRemoveCollaborator}>
            <Translate value="leave_the_group" />
          </Button>
        </WrapperButtons>
      )}
    </Wrapper>
  );
};

GroupInfo.displayName = 'GroupInfo';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const WrapperButtons = styled.div`
 display: flex;
  width: -webkit-fill-available;
  width: stretch;
  align-items: center;
  padding: 10px 0;
  gap: 10px;
  justify-content: center;
  position: fixed;
  bottom: 0;
  background: white;
  z-index: 1;
  height: 58px;
   margin-left: -10px;`;
